/**
 * Legacy-site redirect table and resolver.
 *
 * When miinews.com is pointed at this site, links published against the old
 * React app (miinews-rrc, react-router, case-insensitive paths) must still
 * land on the equivalent page. This module is pure: no router, no fetch, so
 * it can be unit-tested with plain node. The catch-all route in routes/$.tsx
 * wires it in and adds the runtime API fallbacks the table cannot answer
 * (articles imported after the table was generated).
 *
 * The same tables translate links held in the database when they are drawn
 * (internalHrefFor, at the end of this file), so an award still stored with
 * an old address goes straight to its article.
 *
 * Legacy public URL inventory (miinews-rrc/src/siteRoutes.js + SiteLayout):
 *   /                               home                 -> /welcome (served by / already)
 *   /Article?id=<contentlistitemid> article              -> /news/<slug>
 *   /<permalink>                    article by permalink -> /news/<slug>
 *   /aesthetic-insights             news list            -> /news
 *   /about                          about page           -> /p/about-us
 *   /contact                        contact form         -> /p/contact
 *   /strategic-research             research / store     -> /research
 *   /cart                           store cart           -> /research (cart is a sheet now)
 *   /market-studies, /market-studies/index.html#<study>, /market-studies#<study>
 *                                   pre-React store page -> /research (no per-study anchors
 *                                                           on the new page; 158 newsletter/
 *                                                           article links, 2026-09 inventory)
 *   /aesthetic-insights/post/<permalink>, /aesthetic-insights/<permalink>
 *                                   older article URLs   -> /news/<slug> (same permalink rules)
 *   /aesthetic-industry-association AIA page             -> /p/industry-association
 *     #aia-board / #aia-awards / #aia-leadership-awards / #aia-sponsors / #join / #Membership
 *                                                        -> /board, /awards, /awards, /sponsors, /p/industry-association
 *   /gallery, /gallery/<videoId>    video gallery        -> /gallery (new route keeps the permalink)
 *   /gallery?video=<permalink>      newsletter deep link -> /gallery/<permalink>
 *   /gallery/attachment/<n>         WordPress-era images -> /gallery
 *   /beauty, /beauty/<videoId>      The Beauty Show      -> /beauty-show, /beauty-show/<permalink>
 *   /beauty?video=<permalink>       newsletter deep link -> /beauty-show/<permalink>
 *   /aesthetic-industry-association/survey (and any other sub-path)
 *                                   retired AIA survey   -> /p/industry-association
 *   /ax-incubator                   AX Incubator         -> /p/ax-incubator
 *   /privacy-statement              privacy              -> /p/privacy
 *   /terms-and-conditions           terms                -> /p/terms
 *   /login                          admin sign-in        -> /auth
 *   /forum, /forum-faculty-bios, /forum-schedule, /forum-registration,
 *   /forum/<anything>, /aesthetic-accelerator, /ax-brokers, /theme
 *                                   retired pages        -> /welcome
 *
 * Not in any published link (2026-09 inventory of 208 newsletters + 208
 * article bodies + 6 CMS pages), so deliberately unmapped: /subscribe,
 * /newsletter, /unsubscribe, /events, /aia-*, /market-study/<x>, /product/<x>.
 */
import { LEGACY_ARTICLES } from "./legacy-article-ids";

/** Exact legacy pathname (lower-case) -> new pathname. */
export const LEGACY_PATH_MAP: Readonly<Record<string, string>> = {
  "/about": "/p/about-us",
  "/contact": "/p/contact",
  "/privacy-statement": "/p/privacy",
  "/terms-and-conditions": "/p/terms",
  "/strategic-research": "/research",
  "/cart": "/research",
  // The pre-React store page. Newsletters link it as /market-studies,
  // /market-studies#<study> and /market-studies/index.html#<study> (the last
  // is a file request, so it also needs the edge router to hand it to the
  // SPA; see LEGACY_PREFIX_MAP for the sub-path form).
  "/market-studies": "/research",
  "/aesthetic-insights": "/news",
  "/aesthetic-industry-association": "/p/industry-association",
  "/beauty": "/beauty-show",
  "/gallery": "/gallery",
  "/ax-incubator": "/p/ax-incubator",
  "/login": "/auth",
  // Retired programmes with no equivalent page: land on the homepage rather
  // than a 404.
  "/forum": "/welcome",
  "/forum-faculty-bios": "/welcome",
  "/forum-schedule": "/welcome",
  "/forum-registration": "/welcome",
  "/aesthetic-accelerator": "/welcome",
  "/ax-brokers": "/welcome",
  "/theme": "/welcome",
};

/**
 * Anchors the legacy nav used on the single AIA page. The hash never reaches
 * the server but the SPA sees it, so a bookmarked "#aia-board" still lands on
 * the dedicated page. Keys are lower-case, without the "#" (TanStack's
 * `location.hash` omits it; `window.location.hash` keeps it — both accepted).
 */
export const LEGACY_AIA_HASH_MAP: Readonly<Record<string, string>> = {
  "aia-board": "/board",
  "aia-awards": "/awards",
  "aia-leadership-awards": "/awards",
  "aia-sponsors": "/sponsors",
  join: "/p/industry-association",
  membership: "/p/industry-association",
};

/**
 * Prefix rules, checked in order after the exact map. `keepRest` = true
 * appends what followed the prefix (`/beauty/<id>` -> `/beauty-show/<id>`,
 * same permalinks); false collapses every sub-path onto one page.
 */
const LEGACY_PREFIX_MAP: ReadonlyArray<readonly [from: string, to: string, keepRest: boolean]> = [
  ["/beauty/", "/beauty-show/", true],
  // WordPress-era `/gallery/attachment/<n>` image pages: nothing to map them
  // to but the gallery itself. (`/gallery/<videoId>` never reaches this
  // module: the new /gallery/$permalink route owns it.)
  ["/gallery/attachment/", "/gallery", false],
  // `/market-studies/index.html#<study>` and any other sub-path of the old
  // store page.
  ["/market-studies/", "/research", false],
  // `/forum/registration` etc.: the forum is retired (see the exact map).
  ["/forum/", "/welcome", false],
  // `/aesthetic-industry-association/survey`: the survey is retired; the
  // AIA page is the closest thing.
  ["/aesthetic-industry-association/", "/p/industry-association", false],
];

/**
 * `/gallery?video=<permalink>` and `/beauty?video=<permalink>` were the deep
 * links the newsletters used for a single video. The new site addresses the
 * same permalinks by path. (routes/gallery.tsx also accepts `?video=` itself;
 * this rule matters for `/beauty`, which no new route owns.)
 */
const LEGACY_VIDEO_QUERY_MAP: Readonly<Record<string, string>> = {
  "/gallery": "/gallery/",
  "/beauty": "/beauty-show/",
};

/**
 * Older article URL shapes that wrapped the permalink in a folder. Stripped
 * before the permalink lookup so `/aesthetic-insights/post/<permalink>`
 * resolves exactly like `/<permalink>`.
 */
const LEGACY_ARTICLE_PREFIXES: readonly string[] = ["/aesthetic-insights/post/", "/aesthetic-insights/"];

/** Query keys carried across a redirect so campaign attribution survives. */
const PRESERVED_PARAM = /^(utm_[a-z0-9_]+|gclid|fbclid)$/i;

const articleById = new Map<number, string>();
const articleByPermalink = new Map<string, string>();
for (const [listItemId, contentId, permalink, slug] of LEGACY_ARTICLES) {
  articleById.set(listItemId, slug);
  articleById.set(contentId, slug);
  articleByPermalink.set(permalink, slug);
}

/** Same normalisation scripts/import-insights.mjs used to mint the new slugs. */
export function normalizeLegacySlug(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);
}

function cleanPath(pathname: string): string {
  let p = (pathname ?? "").trim().toLowerCase();
  try {
    p = decodeURIComponent(p);
  } catch {
    // keep the raw value
  }
  p = p.replace(/\/+$/, "");
  return p === "" ? "/" : p;
}

function toParams(search: string | URLSearchParams): URLSearchParams {
  return typeof search === "string" ? new URLSearchParams(search) : search;
}

/**
 * Re-attach the preserved query params (and any hash) to a new path.
 * `search` may be a raw "?a=b" string or already-parsed URLSearchParams.
 */
export function withPreservedParams(
  to: string,
  search: string | URLSearchParams = "",
  hash = "",
): string {
  const kept = new URLSearchParams();
  toParams(search).forEach((value, key) => {
    if (PRESERVED_PARAM.test(key)) kept.append(key, value);
  });
  const [toPath, toQuery = ""] = to.split("?", 2);
  const merged = new URLSearchParams(toQuery);
  kept.forEach((value, key) => merged.append(key, value));
  const qs = merged.toString();
  const h = hash && !to.includes("#") ? (hash.startsWith("#") ? hash : `#${hash}`) : "";
  return `${toPath}${qs ? `?${qs}` : ""}${h}`;
}

/** `/Article?id=496` (any case) -> 496; anything else -> null. */
export function parseLegacyArticleId(
  pathname: string,
  search: string | URLSearchParams = "",
): number | null {
  if (cleanPath(pathname) !== "/article") return null;
  const raw = toParams(search).get("id")?.trim();
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 ? id : null;
}

/** Static article lookup by either legacy id space; null when not in the table. */
export function legacyArticleSlugById(id: number): string | null {
  return articleById.get(id) ?? null;
}

/**
 * The permalink segment of a cleaned path: `/<permalink>` or one of the
 * LEGACY_ARTICLE_PREFIXES forms. Null for the root, nested paths and file
 * names (those cannot be permalinks).
 */
function legacyPermalinkSegment(p: string): string | null {
  let segment = p.slice(1);
  for (const prefix of LEGACY_ARTICLE_PREFIXES) {
    if (p.startsWith(prefix) && p.length > prefix.length) {
      segment = p.slice(prefix.length);
      break;
    }
  }
  if (!segment || segment.includes("/")) return null;
  if (/\.[a-z0-9]{1,5}$/i.test(segment)) return null;
  return segment;
}

/**
 * Legacy `/:articleSlug` — any single-segment path the old app did not own
 * was an article permalink. Returns the slugs to try, best first: the static
 * table's answer, then the import's slug forms (`<norm>`, `<norm>-v2`) for
 * the API. Empty for anything that cannot be a permalink (nested paths, file
 * names).
 */
export function legacyArticleSlugCandidates(pathname: string): string[] {
  const segment = legacyPermalinkSegment(cleanPath(pathname));
  if (!segment) return [];
  const norm = normalizeLegacySlug(segment);
  if (!norm) return [];
  const out: string[] = [];
  const known = articleByPermalink.get(norm);
  if (known) out.push(known);
  for (const c of [norm, `${norm}-v2`]) if (!out.includes(c)) out.push(c);
  return out;
}

/**
 * Synchronous resolver: the new href (campaign params preserved) for a legacy
 * URL, or null when nothing in the static tables matches. Callers then try
 * the runtime lookups (parseLegacyArticleId / legacyArticleSlugCandidates)
 * before falling through to not-found.
 */
export function resolveLegacyPath(
  pathname: string,
  search: string | URLSearchParams = "",
  hash = "",
): string | null {
  const p = cleanPath(pathname);
  const h = (hash ?? "").replace(/^#/, "").trim().toLowerCase();

  // /Article?id=N via the static table.
  if (p === "/article") {
    const id = parseLegacyArticleId(p, search);
    const slug = id === null ? null : legacyArticleSlugById(id);
    return slug ? withPreservedParams(`/news/${slug}`, search) : null;
  }

  if (p === "/aesthetic-industry-association" && h && LEGACY_AIA_HASH_MAP[h]) {
    return withPreservedParams(LEGACY_AIA_HASH_MAP[h], search);
  }

  // /gallery?video=<permalink>, /beauty?video=<permalink>. Permalinks are
  // case-sensitive on the new site, so the value is passed through as-is.
  const videoBase = LEGACY_VIDEO_QUERY_MAP[p];
  const video = videoBase ? toParams(search).get("video")?.trim() : undefined;
  if (videoBase && video) {
    return withPreservedParams(`${videoBase}${encodeURIComponent(video)}`, search, hash);
  }

  const exact = LEGACY_PATH_MAP[p];
  if (exact) return withPreservedParams(exact, search, hash);

  for (const [from, to, keepRest] of LEGACY_PREFIX_MAP) {
    if (p.startsWith(from) && p.length > from.length) {
      return withPreservedParams(keepRest ? `${to}${p.slice(from.length)}` : to, search, hash);
    }
  }

  // /<permalink> known from the table (unknown permalinks need the API).
  const segment = legacyPermalinkSegment(p);
  if (segment) {
    const known = articleByPermalink.get(normalizeLegacySlug(segment));
    if (known) return withPreservedParams(`/news/${known}`, search);
  }

  return null;
}

/* ---------------------------------------------------------- stored links */

/**
 * Hosts that ARE this site: the canonical host, the apex (301s to www) and
 * the pre-cutover alias. `www.miiapp.org` is the legacy site and is
 * deliberately absent.
 */
const OWN_HOSTS: ReadonlySet<string> = new Set(["miinews.com", "www.miinews.com", "miiapp.org"]);

/**
 * First segments the new site serves. An absolute address on one of our own
 * hosts is only turned into an in-app path when it points at one of these;
 * `/media/...` files and unknown paths stay ordinary links.
 */
const SITE_PATH_ROOTS: readonly string[] = [
  "/news",
  "/newsletter",
  "/p",
  "/research",
  "/awards",
  "/board",
  "/sponsors",
  "/gallery",
  "/beauty-show",
  "/welcome",
  "/home",
];

/** A path on this site: one leading slash. Two slashes, or slash then backslash, leave the site. */
const SITE_PATH = /^\/(?![/\\])/;

/** True when `href` (as returned by internalHrefFor) is a path on this site. */
export function isInternalHref(href: string | null | undefined): boolean {
  return typeof href === "string" && SITE_PATH.test(href);
}

/**
 * Where a stored link (aia_awards.article_url and the like) really goes.
 *
 *   "/news/<slug>"                       -> returned as is
 *   "https://miinews.com/Article?id=N"   -> "/news/<slug>" when N is in the table
 *   "https://www.miinews.com/news/<slug>"-> "/news/<slug>" (already a site path)
 *   anything else                        -> returned unchanged: an external link
 *
 * Check the result with isInternalHref(). Null, undefined and blank give "".
 * Never throws: a value that cannot be parsed is returned unchanged.
 */
export function internalHrefFor(url: string | null | undefined): string {
  if (typeof url !== "string") return "";
  const raw = url.trim();
  if (!raw || raw.startsWith("/")) return raw;
  try {
    const u = new URL(raw);
    if (!/^https?:$/.test(u.protocol) || !OWN_HOSTS.has(u.hostname.toLowerCase())) return raw;
    const resolved = resolveLegacyPath(u.pathname, u.search, u.hash);
    if (resolved) return resolved;
    const p = cleanPath(u.pathname);
    const onSite = SITE_PATH_ROOTS.some((root) => p === root || p.startsWith(`${root}/`));
    return onSite ? `${u.pathname}${u.search}${u.hash}` : raw;
  } catch {
    return raw;
  }
}

/**
 * What may be stored in a link column: a site path ("/news/<slug>") or a full
 * https:// address. Returns a plain message, or null when the value is fine
 * (blank is fine: the link is optional). people-api's awardSchema applies the
 * same rule on save.
 */
export function storedLinkProblem(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  const message = "Enter a site path starting with / or a full address starting with https://";
  if (/\s/.test(v)) return "The address cannot contain spaces";
  if (SITE_PATH.test(v)) return null;
  if (!/^https:\/\//i.test(v)) return message;
  try {
    return new URL(v).hostname ? null : message;
  } catch {
    return message;
  }
}
